<template>
  <div class="format-cards-section">
    <h2 class="section-title">{{ $t('ebook.formatCards.title') }}</h2>
    <div class="format-cards-grid">
      <div
        v-for="format in formats"
        :key="format.name"
        class="format-card"
      >
        <div class="card-header">
          <Icon :name="format.icon" size="40" class="format-icon" />
          <h3 class="format-name">{{ format.displayName }}</h3>
        </div>

        <!-- 格式图片展示 -->
        <div class="card-image">
          <img :src="format.image" :alt="format.displayName" />
        </div>

        <div class="card-content">
          <div class="format-details">
            <div class="detail-item">
              <Icon name="material-symbols:folder-open" size="16" />
              <span>{{ $t('ebook.formatCards.labels.supported') }}{{ format.extensions.join(', ') }}</span>
            </div>
            <div class="detail-item">
              <Icon name="material-symbols:check-circle" size="16" />
              <span>{{ $t('ebook.formatCards.labels.preserve') }}{{ format.preserves.join('、') }}</span>
            </div>
            <div class="detail-item">
              <Icon name="material-symbols:star" size="16" />
              <span>{{ $t('ebook.formatCards.labels.use') }}{{ format.useCases.join('、') }}</span>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <div class="compatibility-badge">
            <Icon name="material-symbols:devices" size="14" />
            <span>{{ $t('ebook.formatCards.compatibility') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// 电子书格式数据（i18n）
const formats = computed(() => ([
  {
    name: 'epub',
    displayName: t('ebook.formatCards.items.epub.name'),
    icon: 'material-symbols:menu-book',
    image: '/images/ebook/epub-format.jpg',
    extensions: ['.epub'],
    preserves: [
      t('ebook.formatCards.items.epub.preserves.0'),
      t('ebook.formatCards.items.epub.preserves.1'),
      t('ebook.formatCards.items.epub.preserves.2'),
      t('ebook.formatCards.items.epub.preserves.3')
    ],
    useCases: [
      t('ebook.formatCards.items.epub.useCases.0'),
      t('ebook.formatCards.items.epub.useCases.1'),
      t('ebook.formatCards.items.epub.useCases.2')
    ],
    color: '#4A90E2'
  },
  {
    name: 'mobi',
    displayName: t('ebook.formatCards.items.mobi.name'),
    icon: 'material-symbols:devices',
    image: '/images/ebook/mobi-format.jpg',
    extensions: ['.mobi'],
    preserves: [
      t('ebook.formatCards.items.mobi.preserves.0'),
      t('ebook.formatCards.items.mobi.preserves.1'),
      t('ebook.formatCards.items.mobi.preserves.2'),
      t('ebook.formatCards.items.mobi.preserves.3')
    ],
    useCases: [
      t('ebook.formatCards.items.mobi.useCases.0'),
      t('ebook.formatCards.items.mobi.useCases.1'),
      t('ebook.formatCards.items.mobi.useCases.2')
    ],
    color: '#FF8C42'
  },
  {
    name: 'azw',
    displayName: t('ebook.formatCards.items.azw.name'),
    icon: 'material-symbols:import-contacts',
    image: '/images/ebook/azw-format.jpg',
    extensions: ['.azw', '.azw3'],
    preserves: [
      t('ebook.formatCards.items.azw.preserves.0'),
      t('ebook.formatCards.items.azw.preserves.1'),
      t('ebook.formatCards.items.azw.preserves.2'),
      t('ebook.formatCards.items.azw.preserves.3')
    ],
    useCases: [
      t('ebook.formatCards.items.azw.useCases.0'),
      t('ebook.formatCards.items.azw.useCases.1'),
      t('ebook.formatCards.items.azw.useCases.2')
    ],
    color: '#52C41A'
  }
]))
</script>

<style scoped lang="scss">
@use 'sass:color';

.format-cards-section {
  padding: $spacing-xxl 0;
}

.section-title {
  text-align: center;
  font-size: 2.2rem;
  font-weight: 700;
  color: $gray-900;
  margin: 0 0 $spacing-xl 0;

  background: linear-gradient(135deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.format-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: $spacing-xl;
  max-width: 1100px;
  margin: 0 auto;
}

.format-card {
  background: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-lg;
  box-shadow: 0 4px 20px rgba($black, 0.08);
  border: 1px solid $gray-200;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba($black, 0.15);
    border-color: #4A90E2;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4A90E2 0%, #FF8C42 50%, #52C41A 100%);
  }

  &:nth-child(1)::before {
    background: linear-gradient(90deg, #4A90E2 0%, color.adjust(#4A90E2, $lightness: 10%) 100%);
  }

  &:nth-child(2)::before {
    background: linear-gradient(90deg, #FF8C42 0%, color.adjust(#FF8C42, $lightness: 10%) 100%);
  }

  &:nth-child(3)::before {
    background: linear-gradient(90deg, #52C41A 0%, color.adjust(#52C41A, $lightness: 10%) 100%);
  }
}

.card-header {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  margin-bottom: $spacing-md;

  .format-icon {
    color: #4A90E2;
    filter: drop-shadow(0 2px 4px rgba(#4A90E2, 0.3));
  }

  .format-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
  }
}

.card-image {
  margin-bottom: $spacing-lg;
  border-radius: $border-radius-md;
  overflow: hidden;

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
}

.card-content {
  margin-bottom: $spacing-lg;
}

.format-details {
  display: flex;
  flex-direction: column;
  gap: $spacing-sm;

  .detail-item {
    display: flex;
    align-items: flex-start;
    gap: $spacing-sm;
    font-size: 0.9rem;
    color: $gray-700;
    line-height: 1.5;

    .iconify {
      color: #52C41A;
      margin-top: 2px;
      flex-shrink: 0;
    }

    span {
      flex: 1;
    }
  }
}

.card-footer {
  display: flex;
  justify-content: center;
  padding-top: $spacing-md;
  border-top: 1px solid $gray-100;

  .compatibility-badge {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    background: color.adjust(#4A90E2, $lightness: 45%);
    color: #4A90E2;
    padding: $spacing-xs $spacing-md;
    border-radius: $border-radius-md;
    font-size: 0.8rem;
    font-weight: 500;

    .iconify {
      color: #4A90E2;
    }
  }
}

// 悬停效果增强
.format-card {
  &:nth-child(1):hover {
    .format-icon {
      color: color.adjust(#4A90E2, $lightness: -10%);
      transform: scale(1.1);
    }
  }

  &:nth-child(2):hover {
    .format-icon {
      color: color.adjust(#FF8C42, $lightness: -10%);
      transform: scale(1.1);
    }
  }

  &:nth-child(3):hover {
    .format-icon {
      color: color.adjust(#52C41A, $lightness: -10%);
      transform: scale(1.1);
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .section-title {
    font-size: 1.8rem;
  }

  .format-cards-grid {
    grid-template-columns: 1fr;
    gap: $spacing-lg;
  }

  .format-card {
    padding: $spacing-md;
  }

  .card-header {
    flex-direction: column;
    text-align: center;
    gap: $spacing-sm;

    .format-name {
      font-size: 1.2rem;
    }
  }
}

@media (max-width: 480px) {
  .format-details {
    .detail-item {
      font-size: 0.85rem;
      gap: $spacing-xs;
    }
  }
}

// 卡片进入动画
.format-card {
  animation: slideInUp 0.6s ease-out;

  &:nth-child(2) {
    animation-delay: 0.1s;
  }

  &:nth-child(3) {
    animation-delay: 0.2s;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
